import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Button, Card, Spinner } from "../components/ui/primitives";
import { api, unwrap } from "../lib/api";

export function AdminPage() {
  const qc = useQueryClient();
  const stats = useQuery({ queryKey: ["admin"], queryFn: async () => unwrap(await api.admin.stats.$get()), refetchInterval: 15_000 });
  const clear = useMutation({
    mutationFn: async () => unwrap(await api.admin.cache.clear.$post()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  if (stats.isLoading || !stats.data) return <div className="grid h-full place-items-center"><Spinner /></div>;
  const s = stats.data;
  const pct = Math.min(100, (s.budget.spent_usd / s.budget.limit_usd) * 100);

  return (
    <div className="h-full overflow-y-auto px-3 py-4 sm:px-6">
      <div className="mx-auto max-w-4xl space-y-4">
        <Card className="p-4">
          <p className="text-sm text-roast/80">Gasto de IA (OpenRouter)</p>
          <p className="text-2xl font-semibold tabular-nums">
            ${s.budget.spent_usd.toFixed(4)} <span className="text-base font-normal text-roast/70">de ${s.budget.limit_usd} USD</span>
          </p>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-latte">
            <div className="h-full rounded-full bg-caramel" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-xs text-roast/70">
            Modelo principal: <code>{s.models.primary}</code> · utilitario: <code>{s.models.utility}</code> · {s.conversations} conversaciones
          </p>
        </Card>

        <div className="grid gap-4 sm:grid-cols-2">
          <Card className="p-4">
            <p className="mb-2 font-medium">Llamadas al modelo</p>
            <table className="w-full text-xs tabular-nums">
              <thead>
                <tr className="text-left text-roast/70">
                  <th>Modelo / origen</th>
                  <th className="text-right">Llamadas</th>
                  <th className="text-right">Tokens in (caché)</th>
                  <th className="text-right">USD</th>
                </tr>
              </thead>
              <tbody>
                {s.llm.map((r) => (
                  <tr key={`${r.model}-${r.source}`} className="border-t border-latte/60">
                    <td className="py-1">
                      {r.model.replace("anthropic/", "")} · {r.source}
                    </td>
                    <td className="text-right">{r.calls}</td>
                    <td className="text-right">
                      {r.input_tokens} ({r.cached_input_tokens})
                    </td>
                    <td className="text-right">{Number(r.cost_usd).toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card className="p-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="font-medium">Caché de la API de Luyoa</p>
              <Button variant="secondary" className="px-2.5 py-1 text-xs" onClick={() => clear.mutate()} disabled={clear.isPending}>
                <RefreshCw className="size-3.5" /> Vaciar
              </Button>
            </div>
            <p className="text-sm tabular-nums">
              {s.cache.entries} respuestas guardadas · {s.cache.hits} aciertos
            </p>
            <p className="mt-1 text-xs text-roast/70">El dataset es una foto fija, así que la caché evita llamadas repetidas y respeta el límite de 120 req/min.</p>
          </Card>
        </div>

        <Card className="p-4">
          <p className="mb-2 font-medium">Herramientas del agente</p>
          <table className="w-full text-xs tabular-nums">
            <thead>
              <tr className="text-left text-roast/70">
                <th>Herramienta</th>
                <th className="text-right">Llamadas</th>
                <th className="text-right">Errores</th>
                <th className="text-right">Latencia prom.</th>
              </tr>
            </thead>
            <tbody>
              {s.tools.map((t) => (
                <tr key={t.name} className="border-t border-latte/60">
                  <td className="py-1 font-mono">{t.name}</td>
                  <td className="text-right">{t.calls}</td>
                  <td className="text-right">{t.errors}</td>
                  <td className="text-right">{t.avg_latency_ms} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
          {s.recent_tool_errors.length > 0 && (
            <div className="mt-3 space-y-1 text-xs">
              <p className="font-medium">Errores recientes</p>
              {s.recent_tool_errors.map((e, i) => (
                <p key={i} className="text-roast/80">
                  <code>{e.name}</code>: {e.error}
                </p>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
